import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { useBoatBenchmark } from '../hooks/useBoatBenchmark';
import IOSheet from './IOSheet';
import MarketPriceTransparency, { type MarketPosition } from './MarketPriceTransparency';

interface MarketPriceProps {
  boatId: number;
  price?: number | null;
  compact?: boolean;
}

type Bucket = 'in_line' | 'below' | 'above';

const formatEuro = (v: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

const BADGE_STYLE: Record<Bucket, { bg: string; border: string; color: string; label: string }> = {
  in_line: {
    bg: '#ecfdf5',
    border: '#a7f3d0',
    color: '#047857',
    label: 'In linea col mercato',
  },
  below: {
    bg: '#eff6ff',
    border: '#bfdbfe',
    color: '#1d4ed8',
    label: 'Sotto la media di mercato',
  },
  above: {
    bg: '#fffbeb',
    border: '#fde68a',
    color: '#b45309',
    label: 'Sopra la media di mercato',
  },
};

export default function MarketPrice({ boatId, price, compact = false }: MarketPriceProps) {
  const { data, isLoading } = useBoatBenchmark(boatId);
  const group = data?.data;
  const [sheetOpen, setSheetOpen] = useState(false);

  const position = useMemo<MarketPosition | null>(() => {
    if (!group || !price || price <= 0) return null;
    const median = group.median_price;
    if (!median || median <= 0) return null;
    const iqr = group.iqr && group.iqr > 0 ? group.iqr : 0;
    let bucket: Bucket;
    if (iqr > 0) {
      if (price < median - iqr) bucket = 'below';
      else if (price > median + iqr) bucket = 'above';
      else bucket = 'in_line';
    } else {
      bucket = price < median ? 'below' : 'above';
    }
    const deltaPercent = Math.round(((price - median) / median) * 100);
    return { bucket, deltaPercent, median, iqr };
  }, [group, price]);

  // Gauge geometry (full mode only). Kept unconditional so hook order never
  // changes between renders (React error #310).
  const gauge = useMemo(() => {
    if (compact || !position || !group) return null;
    const { median, iqr } = position;
    const iqrLow = iqr > 0 ? median - iqr : median;
    const iqrHigh = iqr > 0 ? median + iqr : median;
    const low = group.low_outlier_threshold && group.low_outlier_threshold < iqrLow ? group.low_outlier_threshold : iqrLow;
    const high = group.high_outlier_threshold && group.high_outlier_threshold > iqrHigh ? group.high_outlier_threshold : iqrHigh;
    if (high <= low) return null;
    const clamp = (v: number) => Math.max(0, Math.min(100, ((v - low) / (high - low)) * 100));
    return {
      low,
      high,
      iqrLowPct: clamp(iqrLow),
      iqrHighPct: clamp(iqrHigh),
      medianPct: clamp(median),
      pricePct: clamp(price!),
    };
  }, [compact, group, position, price]);

  if (isLoading || !position || !group) return null;

  const badge = BADGE_STYLE[position.bucket];
  const freshnessDays = group.transparency?.freshness_days;

  return (
    <div className="flex flex-col gap-1.5 w-full" data-testid="market-price">
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold self-start active:opacity-70 cursor-pointer"
        style={{ backgroundColor: badge.bg, border: `1px solid ${badge.border}`, color: badge.color }}
        title={`Mediana di mercato: ${formatEuro(position.median)}${position.deltaPercent >= 0 ? ' (+' : ' ('}${position.deltaPercent}% vs mediana)`}
        aria-label={`${badge.label}. Tocca per vedere come è stato calcolato il valore di mercato.`}
      >
        <span
          className="w-1.5 h-1.5 rounded-full inline-block"
          style={{ backgroundColor: badge.color }}
        />
        {badge.label}
        <Info className="w-3 h-3 opacity-60 shrink-0" />
      </button>

      {gauge && (
        <div className="w-full">
          <div className="relative h-1.5 rounded-full bg-gray-100" style={{ background: 'var(--ios-light-gray-2, #e5e7eb)' }}>
            <div
              className="absolute inset-y-0 rounded-full"
              style={{
                left: `${gauge.iqrLowPct}%`,
                width: `${gauge.iqrHighPct - gauge.iqrLowPct}%`,
                backgroundColor: 'rgba(16,185,129,0.25)',
              }}
            />
            <div
              className="absolute top-1/2 w-2 h-2 rounded-full border-2 border-white -translate-y-1/2"
              style={{ left: `calc(${gauge.medianPct}% - 4px)`, backgroundColor: '#6b7280', zIndex: 1 }}
              title={`Mediana: ${formatEuro(position.median)}`}
            />
            <div
              className="absolute top-1/2 w-2.5 h-2.5 rounded-full border-2 border-white -translate-y-1/2"
              style={{ left: `calc(${gauge.pricePct}% - 5px)`, backgroundColor: badge.color, zIndex: 2 }}
              title={`Questo annuncio: ${formatEuro(price!)}`}
            />
          </div>
          <div className="flex items-center justify-between text-[9px] text-gray-400 mt-0.5">
            <span>{formatEuro(gauge.low)}</span>
            <span className="font-medium text-gray-500">Mediana {formatEuro(position.median)}</span>
            <span>{formatEuro(gauge.high)}</span>
          </div>
          <p className="text-[9px] text-gray-400 mt-0.5">
            {group.listing_count > 0 && <>su {group.listing_count} annunci</>}
            {group.confidence != null && <> · affidabilità {Math.round(group.confidence)}%</>}
            {freshnessDays != null && <> · dati di {freshnessDays} gg fa</>}
          </p>
        </div>
      )}

      {sheetOpen && (
        <IOSheet
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Valore di mercato"
          snapPoints={[0.55, 0.95]}
          className="max-w-xl mx-auto"
        >
          <MarketPriceTransparency
            group={group}
            price={price!}
            position={position}
            matched={data?.matched}
          />
        </IOSheet>
      )}
    </div>
  );
}
